import Alert from '@risk-smart/themed-cloudscape-components/alert';
import Box from '@risk-smart/themed-cloudscape-components/box';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import Table from '@risksmart-app/components/src/table';
import type { WebhookPartsFragment } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import TabHeader from 'src/components/tab-header';

import { useDeleteWebhook } from '@/hooks/mutations/webhook';
import { useListWebhooks } from '@/hooks/queries';

import { useGetCollectionTableProps } from './config';
import DeleteWebhookModal from './modals/DeleteWebhookModal';
import WebhookDeliveryLogsModal from './modals/WebhookDeliveryLogsModal';
import WebhookFormModal from './modals/WebhookFormModal';

const MAX_WEBHOOKS = 10;

const WebhooksTab: FC = () => {
  useI18NSummaryHelpContent('webhooks.help');
  const { t } = useTranslation(['common'], { keyPrefix: 'webhooks' });
  const { addNotification } = useNotifications();

  const {
    data,
    loading,
    error: webhooksError,
    refetch,
  } = useListWebhooks({ queryArgs: undefined });
  const { deleteWebhook } = useDeleteWebhook();
  const webhooks: WebhookPartsFragment[] = data?.organisation_webhook ?? [];
  const hasError = !!webhooksError;
  const isCreateDisabled = webhooks.length >= MAX_WEBHOOKS;

  const [selectedWebhooks, setSelectedWebhooks] = useState<
    WebhookPartsFragment[]
  >([]);
  const [editingWebhook, setEditingWebhook] =
    useState<WebhookPartsFragment | null>(null);
  const [viewingLogsWebhook, setViewingLogsWebhook] =
    useState<WebhookPartsFragment | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  // Tracks the whole multi-row delete batch. `useMutation.isPending` from a
  // single hook only reflects one call at a time, so it can't represent N
  // concurrent deletes.
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);

  const handleEdit = (webhook: WebhookPartsFragment) => {
    setEditingWebhook(webhook);
  };

  const tableProps = useGetCollectionTableProps(webhooks, handleEdit);

  const handleCreateSuccess = () => {
    setIsCreateModalOpen(false);
    void refetch();
  };

  const handleEditSuccess = () => {
    setEditingWebhook(null);
    void refetch();
  };

  const handleDelete = async () => {
    setIsDeletingBatch(true);
    try {
      const results = await Promise.allSettled(
        selectedWebhooks.map((webhook) => deleteWebhook(webhook.Id))
      );
      const failed = results.flatMap((result, index) =>
        result.status === 'rejected' ? [selectedWebhooks[index]] : []
      );
      const succeededCount = selectedWebhooks.length - failed.length;
      const failedTitles = failed.map((w) => w.Title).join(', ');

      if (failed.length === 0) {
        addNotification({
          type: 'success',
          content: t('delete_modal.success_message', {
            count: succeededCount,
          }),
        });
      } else if (succeededCount === 0) {
        addNotification({
          type: 'error',
          content: t('delete_modal.error_message', {
            count: failed.length,
            titles: failedTitles,
          }),
        });
      } else {
        addNotification({
          type: 'error',
          content: t('delete_modal.partial_error_message', {
            succeededCount,
            failedCount: failed.length,
            titles: failedTitles,
          }),
        });
      }

      setIsDeleteModalVisible(false);
      // Keep failed rows selected so the user can retry without re-selecting;
      // succeeded rows disappear when the refetch lands.
      setSelectedWebhooks(failed);
    } finally {
      setIsDeletingBatch(false);
      void refetch();
    }
  };

  return (
    <>
      <Table
        {...tableProps}
        header={
          <SpaceBetween size={'m'}>
            <TabHeader
              actions={
                <SpaceBetween direction={'horizontal'} size={'xs'}>
                  <Button formAction={'none'} variant={'normal'} disabled>
                    {t('send_test_button')}
                  </Button>
                  <Button
                    formAction={'none'}
                    variant={'normal'}
                    disabled={hasError || selectedWebhooks.length !== 1}
                    onClick={() => setViewingLogsWebhook(selectedWebhooks[0])}
                  >
                    {t('view_logs_button')}
                  </Button>
                  <Button
                    formAction={'none'}
                    variant={'normal'}
                    disabled={hasError || selectedWebhooks.length === 0}
                    onClick={() => setIsDeleteModalVisible(true)}
                  >
                    {t('delete_modal.delete_button')}
                  </Button>
                  <Button
                    variant={'primary'}
                    formAction={'none'}
                    onClick={() => setIsCreateModalOpen(true)}
                    disabled={hasError || isCreateDisabled}
                  >
                    {t('add_button')}
                  </Button>
                </SpaceBetween>
              }
            >
              {t('tab_title')}
            </TabHeader>
            <SpaceBetween key={'alerts-container'} size={'xs'}>
              {hasError && (
                <Alert key={'load-error'} type={'error'}>
                  {t('load_error_message')}
                </Alert>
              )}
              {!loading && !hasError && isCreateDisabled && (
                <Alert key={'mc-warn'} type={'warning'}>
                  {t('max_webhooks_warning')}
                </Alert>
              )}
              <Box key={'info-msg'} margin={{ bottom: 'xs' }}>
                <Alert type={'info'}>{t('info_message')}</Alert>
              </Box>
            </SpaceBetween>
          </SpaceBetween>
        }
        variant={'embedded'}
        loading={loading}
        selectionType={'multi'}
        selectedItems={selectedWebhooks}
        onSelectionChange={({ detail }) => {
          setSelectedWebhooks(detail.selectedItems);
        }}
        trackBy={'Id'}
      />
      {isCreateModalOpen && (
        <WebhookFormModal
          onDismiss={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
        />
      )}
      {editingWebhook && (
        <WebhookFormModal
          webhook={editingWebhook}
          onDismiss={() => setEditingWebhook(null)}
          onSuccess={handleEditSuccess}
        />
      )}
      <DeleteWebhookModal
        webhooks={selectedWebhooks}
        isVisible={isDeleteModalVisible}
        onDelete={() => void handleDelete()}
        onDismiss={() => setIsDeleteModalVisible(false)}
        loading={isDeletingBatch}
      />
      {viewingLogsWebhook && (
        <WebhookDeliveryLogsModal
          webhookId={viewingLogsWebhook.Id}
          webhookTitle={viewingLogsWebhook.Title}
          onDismiss={() => setViewingLogsWebhook(null)}
        />
      )}
    </>
  );
};

export default WebhooksTab;
